import axios from '@/libs/axios'

export default {
  namespaced: true,

  state: {
    // Mi calendario
    miCalendario: null,
    misFestivos: [],
    misSolicitudes: [],
    miResumen: null,

    // Admin: Calendarios
    calendarios: [],
    calendarioSeleccionado: null,
    festivosCalendario: [],

    // Admin: Asignaciones
    asignaciones: [],
    usuariosSinCalendario: [],

    // Admin: Convenios
    convenios: [],
    categoriasConvenio: [],

    // Admin: Solicitudes
    solicitudesPendientes: [],
    solicitudesTodas: [],

    // Calendario usuario: datos extra
    vacacionesEquipo: [],
    festivosEquipo: [],
    tareasCalendario: [],

    // UI
    loading: false,
    error: null
  },

  getters: {
    diasDisponibles: state => (state.miResumen && state.miResumen.dias_disponibles) || 0,
    diasUsados: state => (state.miResumen && state.miResumen.dias_aprobados) || 0,
    diasPendientes: state => (state.miResumen && state.miResumen.dias_pendientes) || 0,
    diasTotales: state => (state.miResumen && state.miResumen.dias_totales) || 0,

    eventosCalendario: state => {
      const eventos = []

      // Festivos del calendario (verde, como vacaciones aprobadas)
      state.misFestivos.forEach(f => {
        eventos.push({
          id: `festivo-${f.id}`,
          title: 'Vacaciones - Festivo',
          start: f.fecha,
          allDay: true,
          extendedProps: { tipo: 'festivo', nombre: f.nombre },
          backgroundColor: '#28c76f',
          borderColor: '#28c76f'
        })
      })

      // Mis solicitudes
      state.misSolicitudes.forEach(s => {
        let color = '#ff9f43' // pendiente (naranja)
        if (s.estado === 'aprobada') {
          // Medio día = azul, día completo = verde
          color = (s.tipo === 'manana' || s.tipo === 'tarde') ? '#00cfe8' : '#28c76f'
        }
        if (s.estado === 'rechazada') color = '#ea5455' // rojo

        let title = 'Vacaciones'
        if (s.tipo === 'manana') title = 'Vacaciones (mañana)'
        if (s.tipo === 'tarde') title = 'Vacaciones (tarde)'

        eventos.push({
          id: `solicitud-${s.id}`,
          title,
          start: s.fecha,
          allDay: true,
          extendedProps: {
            tipo: 'solicitud',
            estado: s.estado,
            tipoVacacion: s.tipo,
            solicitudId: s.id,
            motivo: s.motivo_rechazo || null
          },
          backgroundColor: color,
          borderColor: color
        })
      })

      return eventos
    }
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_MI_CALENDARIO(state, data) {
      state.miCalendario = data.calendario
      state.misFestivos = data.festivos || []
      state.misSolicitudes = data.solicitudes || []
      state.miResumen = data.resumen
    },
    SET_MIS_SOLICITUDES(state, solicitudes) {
      state.misSolicitudes = solicitudes
    },
    ADD_SOLICITUD(state, solicitud) {
      state.misSolicitudes.push(solicitud)
    },
    REMOVE_SOLICITUD(state, id) {
      state.misSolicitudes = state.misSolicitudes.filter(s => s.id !== id)
    },

    // Admin
    SET_CALENDARIOS(state, calendarios) {
      state.calendarios = calendarios
    },
    SET_CALENDARIO_SELECCIONADO(state, calendario) {
      state.calendarioSeleccionado = calendario
    },
    SET_FESTIVOS_CALENDARIO(state, festivos) {
      state.festivosCalendario = festivos
    },
    SET_ASIGNACIONES(state, asignaciones) {
      state.asignaciones = asignaciones
    },
    SET_USUARIOS_SIN_CALENDARIO(state, usuarios) {
      state.usuariosSinCalendario = usuarios
    },
    SET_SOLICITUDES_PENDIENTES(state, solicitudes) {
      state.solicitudesPendientes = solicitudes
    },
    SET_SOLICITUDES_TODAS(state, solicitudes) {
      state.solicitudesTodas = solicitudes
    },

    // Calendario usuario: datos extra
    SET_VACACIONES_EQUIPO(state, data) {
      state.vacacionesEquipo = data
    },
    SET_FESTIVOS_EQUIPO(state, data) {
      state.festivosEquipo = data
    },
    SET_TAREAS_CALENDARIO(state, data) {
      state.tareasCalendario = data
    },

    // Convenios
    SET_CONVENIOS(state, convenios) {
      state.convenios = convenios
    },
    SET_CATEGORIAS_CONVENIO(state, categorias) {
      state.categoriasConvenio = categorias
    }
  },

  actions: {
    // =============================================
    // USUARIO
    // =============================================

    async fetchMiCalendario({ commit }, anio) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.get('/vacaciones/mi-calendario', {
          params: { anio },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_MI_CALENDARIO', data)
        return data
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al cargar calendario'
        commit('SET_ERROR', msg)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchMisSolicitudes({ commit }, { anio, estado }) {
      try {
        const { data } = await axios.get('/vacaciones/mis-solicitudes', {
          params: { anio, estado },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_MIS_SOLICITUDES', data)
        return data
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al cargar solicitudes'
        commit('SET_ERROR', msg)
        throw error
      }
    },

    async crearSolicitud({ commit, dispatch }, { fechas, tipo }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.post('/vacaciones/solicitudes', { fechas, tipo }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        // Recargar mi calendario para actualizar resumen
        const anio = new Date(Array.isArray(fechas) ? fechas[0] : fechas).getFullYear()
        await dispatch('fetchMiCalendario', anio)
        return data
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al crear solicitud'
        commit('SET_ERROR', msg)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async cancelarSolicitud({ commit, dispatch }, { id, anio }) {
      try {
        await axios.delete(`/vacaciones/solicitudes/${id}`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('REMOVE_SOLICITUD', id)
        if (anio) await dispatch('fetchMiCalendario', anio)
        return true
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al cancelar solicitud'
        commit('SET_ERROR', msg)
        throw error
      }
    },

    // =============================================
    // USUARIO: DATOS EXTRA CALENDARIO
    // =============================================

    async fetchVacacionesEquipo({ commit }, anio) {
      try {
        const { data } = await axios.get('/vacaciones/equipo', {
          params: { anio },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_VACACIONES_EQUIPO', data.solicitudes || [])
        commit('SET_FESTIVOS_EQUIPO', data.festivos || [])
        return data
      } catch (error) {
        commit('SET_VACACIONES_EQUIPO', [])
        commit('SET_FESTIVOS_EQUIPO', [])
        return []
      }
    },

    async fetchTareasCalendario({ commit }, anio) {
      try {
        const { data } = await axios.get('/tareas/calendario', {
          params: { anio },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_TAREAS_CALENDARIO', data)
        return data
      } catch (error) {
        commit('SET_TAREAS_CALENDARIO', [])
        return []
      }
    },

    // =============================================
    // ADMIN: CALENDARIOS
    // =============================================

    async fetchCalendarios({ commit }) {
      try {
        const { data } = await axios.get('/vacaciones/calendarios', {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_CALENDARIOS', data)
        return data
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al cargar calendarios'
        commit('SET_ERROR', msg)
        throw error
      }
    },

    async crearCalendario({ dispatch }, payload) {
      try {
        const { data } = await axios.post('/vacaciones/calendarios', payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCalendarios')
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async actualizarCalendario({ dispatch }, { id, ...payload }) {
      try {
        await axios.put(`/vacaciones/calendarios/${id}`, payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCalendarios')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async eliminarCalendario({ dispatch }, id) {
      try {
        await axios.delete(`/vacaciones/calendarios/${id}`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCalendarios')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    // =============================================
    // ADMIN: FESTIVOS
    // =============================================

    async fetchFestivos({ commit }, calendarioId) {
      try {
        const { data } = await axios.get(`/vacaciones/calendarios/${calendarioId}/festivos`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_FESTIVOS_CALENDARIO', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearFestivo({ dispatch }, payload) {
      try {
        await axios.post('/vacaciones/festivos', payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchFestivos', payload.calendario_id)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearFestivosBulk({ dispatch }, { calendario_id, festivos }) {
      try {
        await axios.post('/vacaciones/festivos/bulk', { calendario_id, festivos }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchFestivos', calendario_id)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async eliminarFestivo({ dispatch }, { id, calendario_id }) {
      try {
        await axios.delete(`/vacaciones/festivos/${id}`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchFestivos', calendario_id)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    // =============================================
    // ADMIN: ASIGNACIONES
    // =============================================

    async fetchAsignaciones({ commit }, anio) {
      try {
        const { data } = await axios.get('/vacaciones/asignaciones', {
          params: { anio },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_ASIGNACIONES', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async fetchUsuariosSinCalendario({ commit }, anio) {
      try {
        const { data } = await axios.get('/vacaciones/asignaciones/sin-calendario', {
          params: { anio },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_USUARIOS_SIN_CALENDARIO', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearAsignacion({ dispatch }, payload) {
      try {
        await axios.post('/vacaciones/asignaciones', payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        const anio = new Date().getFullYear()
        await dispatch('fetchAsignaciones', anio)
        await dispatch('fetchUsuariosSinCalendario', anio)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearAsignacionesBulk({ dispatch }, payload) {
      try {
        await axios.post('/vacaciones/asignaciones/bulk', payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        const anio = new Date().getFullYear()
        await dispatch('fetchAsignaciones', anio)
        await dispatch('fetchUsuariosSinCalendario', anio)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async asignarConvenioBulk({ dispatch }, { convenio_id, asignacion_ids, anio }) {
      try {
        await axios.put('/vacaciones/asignaciones/convenio-bulk', { convenio_id, asignacion_ids }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchAsignaciones', anio || new Date().getFullYear())
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async actualizarAsignacion({ dispatch }, { usuario_id, calendario_id, ...payload }) {
      try {
        await axios.put(`/vacaciones/asignaciones/${usuario_id}`, { calendario_id, ...payload }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        const anio = new Date().getFullYear()
        await dispatch('fetchAsignaciones', anio)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    // =============================================
    // ADMIN: SOLICITUDES
    // =============================================

    async fetchSolicitudesPendientes({ commit }) {
      try {
        const { data } = await axios.get('/vacaciones/admin/pendientes', {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_SOLICITUDES_PENDIENTES', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async fetchSolicitudesTodas({ commit }, { anio, estado, usuario_id }) {
      try {
        const { data } = await axios.get('/vacaciones/admin/solicitudes', {
          params: { anio, estado, usuario_id },
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_SOLICITUDES_TODAS', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async aprobarSolicitud({ dispatch }, id) {
      try {
        await axios.put(`/vacaciones/admin/solicitudes/${id}/aprobar`, {}, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchSolicitudesPendientes')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async aprobarSolicitudesBulk({ dispatch }, ids) {
      try {
        await axios.put('/vacaciones/admin/solicitudes/aprobar-bulk', { ids }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchSolicitudesPendientes')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async rechazarSolicitud({ dispatch }, { id, motivo }) {
      try {
        await axios.put(`/vacaciones/admin/solicitudes/${id}/rechazar`, { motivo }, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchSolicitudesPendientes')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    // =============================================
    // ADMIN: CONVENIOS
    // =============================================

    async fetchConvenios({ commit }) {
      try {
        const { data } = await axios.get('/vacaciones/convenios', {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_CONVENIOS', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearConvenio({ dispatch }, payload) {
      try {
        const { data } = await axios.post('/vacaciones/convenios', payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchConvenios')
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async actualizarConvenio({ dispatch }, { id, ...payload }) {
      try {
        await axios.put(`/vacaciones/convenios/${id}`, payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchConvenios')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async eliminarConvenio({ dispatch }, id) {
      try {
        await axios.delete(`/vacaciones/convenios/${id}`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchConvenios')
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async fetchCategorias({ commit }, convenioId) {
      try {
        const { data } = await axios.get(`/vacaciones/convenios/${convenioId}/categorias`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        commit('SET_CATEGORIAS_CONVENIO', data)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async crearCategoria({ dispatch }, { convenio_id, ...payload }) {
      try {
        const { data } = await axios.post(`/vacaciones/convenios/${convenio_id}/categorias`, payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCategorias', convenio_id)
        return data
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async actualizarCategoria({ dispatch }, { id, convenio_id, ...payload }) {
      try {
        await axios.put(`/vacaciones/convenios/categorias/${id}`, payload, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCategorias', convenio_id)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    },

    async eliminarCategoria({ dispatch }, { id, convenio_id }) {
      try {
        await axios.delete(`/vacaciones/convenios/categorias/${id}`, {
          headers: { 'x-token': localStorage.getItem('token') }
        })
        await dispatch('fetchCategorias', convenio_id)
      } catch (error) {
        throw (error.response && error.response.data) || error
      }
    }
  }
}
